import type { PropsWithChildren } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { config } from '../lib/config';
import { getStyles, getManifest, getVoices } from '../lib/getVoices';
import type { StyleText, Gender, Voice, VoiceDemoDataType } from '../lib/types';
import { isTokenExpired } from '../lib/utils';
import { loadFeatureFlags } from '../lib/useFeatureFlags';

const getFormattedVoiceData = (voices: Voice[]): VoiceDemoDataType[] => {
  const grouped = voices.reduce(
    (acc, voice) => {
      const locale = voice.models[0].languages[0].locale;
      if (!acc[locale]) {
        acc[locale] = [];
      }
      acc[locale].push(voice);
      return acc;
    },
    {} as Record<string, Voice[]>,
  );

  const languageNames =
    // eslint-disable-next-line compat/compat
    typeof Intl.DisplayNames !== 'undefined'
      ? // eslint-disable-next-line compat/compat
        new Intl.DisplayNames(['en'], { type: 'language' })
      : { of: (code: string) => code }; // Fallback to return the code itself
  const formattedVoice: VoiceDemoDataType[] = [];

  for (const languageCode in grouped) {
    const voices = grouped[languageCode];
    const languageName =
      languageNames.of(languageCode.split('-')[0]) ?? 'en-us';
    formattedVoice.push({
      languageCode,
      languageName,
      voices: voices.map((voice: Voice) => {
        return {
          region: languageCode.split('-')[1]?.toLocaleUpperCase(),
          displayName: voice.display_name,
          name: voice.id,
          gender: voice.gender as Gender,
          engine: voice.type,
          languageCode: languageCode,
          image: voice.avatar_image ?? '',
          imageLarge: voice.avatar_image ?? '',
          customText: '',
          previewAudio: voice.preview_audio!,
          description: '',
          isCelebrity: false,
          models: voice.models.map((model) => model.name),
        };
      }),
    });
  }

  return formattedVoice;
};

interface VoicesContextValue {
  voices: VoiceDemoDataType[];
  refetch: () => Promise<void>;
  error: string | undefined;
  loading: boolean;
  token?: string;
  waitForToken: () => Promise<string>;
  styleText: StyleText;
  staticAudioFilesManifest: string[];
}

const VoicesContext = createContext<VoicesContextValue>({
  voices: [],
  refetch: () => Promise.resolve(),
  error: undefined,
  loading: false,
  token: undefined,
  waitForToken: () => Promise.resolve(''),
  styleText: {},
  staticAudioFilesManifest: [],
});

export function VoicesProvider({ children }: PropsWithChildren) {
  const [voices, setVoices] = useState<VoiceDemoDataType[]>([]);
  const [styleText, setStyleText] = useState<StyleText>({});
  const [staticAudioFilesManifest, setStaticAudioFilesManifest] = useState<
    string[]
  >([]);
  const [isStyleLoading, setIsStyleLoading] = useState(true);
  const [token, setToken] = useState<string>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();
  const tokenRef = useRef<string>();

  const fetchToken = useCallback(async () => {
    const options = {
      method: 'GET',
    };
    const { access_token } = await fetch(
      `${config.demoBackendUrl}/token`,
      options,
    ).then(async (res) => {
      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }
      return (await res.json()) as { access_token: string };
    });
    setToken(access_token);
  }, []);

  const fetchVoices = useCallback(async () => {
    setLoading(true);
    try {
      const currVoices = await getVoices();
      const formattedVoices = getFormattedVoiceData(currVoices);
      setVoices(formattedVoices);
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('An unknown error occurred');
      }
      console.error(err);
      setVoices([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchStyle = useCallback(async () => {
    try {
      const styleText = await getStyles();
      setStyleText(styleText);
    } catch (err) {
      console.error(err);
      return {};
    } finally {
      setIsStyleLoading(false);
    }
  }, []);

  const tokenPromise: Promise<string> = new Promise((resolve, reject) => {
    if (token) {
      resolve(token);
    } else {
      const interval = setInterval(() => {
        if (tokenRef.current) {
          clearInterval(interval);
          resolve(tokenRef.current);
        }
      }, 50);

      setTimeout(() => {
        clearInterval(interval);
        reject(new Error('Token loading timed out'));
      }, 10000);
    }
  });

  const waitForToken = async (): Promise<string> => {
    try {
      let token = await tokenPromise;
      if (isTokenExpired(token)) {
        await fetchToken();
        token = await tokenPromise;
      }
      return token;
    } catch (error) {
      console.error('Error waiting for token:', error);
      throw error;
    }
  };
  const fetchStaticAudioFilesManifest = useCallback(async () => {
    try {
      const manifest = await getManifest();
      setStaticAudioFilesManifest(manifest);
    } catch (err) {
      console.error(err);
      setStaticAudioFilesManifest([]);
    }
  }, []);

  useEffect(() => {
    tokenRef.current = token;
  }, [token]);

  useEffect(() => {
    fetchVoices().catch((err: Error) => {
      throw new Error(err.message);
    });
    fetchToken().catch((err: Error) => {
      throw new Error(err.message);
    });
    void fetchStyle();
    void fetchStaticAudioFilesManifest();
    void loadFeatureFlags();
  }, [fetchVoices, fetchToken, fetchStyle, fetchStaticAudioFilesManifest]);

  const value = useMemo<VoicesContextValue>(
    () => ({
      voices,
      refetch: fetchVoices,
      error,
      loading,
      token,
      waitForToken,
      styleText,
      staticAudioFilesManifest,
    }),
    [
      voices,
      fetchVoices,
      error,
      loading,
      token,
      waitForToken,
      styleText,
      staticAudioFilesManifest,
      isStyleLoading,
    ],
  );

  return (
    <VoicesContext.Provider value={value}>{children}</VoicesContext.Provider>
  );
}

export const useVoices = () => useContext(VoicesContext);
